import {useField} from 'formik';
import _ from 'lodash';
import React, {useMemo} from 'react';
import styled from 'styled-components';

import {FormLabel, Radio} from '../../../ui-toolkit/indexExportV2';

import {unwrapNullableDataType} from '../../../ducks/formattersettingshelpers';
import {useFormatterById} from '../../FormattersContext';
import ToJsonFormatter from './ToJsonFormatter';

function visibleOptions(currentSelection, allOptions) {
    let currentPath = !currentSelection ? [] : currentSelection.split('/');

    return _.filter(allOptions, (o) => {
        let optionPath = o.value.split('/');

        let directDescendent = optionPath.length - 1 === currentPath.length && _.startsWith(o.value, currentSelection);
        let sameLevel = _.isEqual(_.initial(optionPath), _.initial(currentPath));
        let ancestor = _.startsWith(currentSelection, o.value) || optionPath.length === 1;

        return directDescendent || sameLevel || ancestor;
    });
}

function EnumRadioEditor({formatter, fieldName, entityType}) {
    const enumDataType = unwrapNullableDataType(entityType);
    const formatterById = useFormatterById(formatter.settings.entryFormatterId);

    const [FieldFormatter, subFormatter] = useMemo(() => {
        let subFormatter = formatterById;
        let FieldFormatter = {Formatter: ToJsonFormatter};
        if (subFormatter) FieldFormatter = subFormatter;
        return [FieldFormatter, subFormatter];
    }, [formatter.settings.entryFormatterId]);

    const [field] = useField(fieldName);

    const options = useMemo(
        () =>
            formatter.settings.sorted !== false
                ? _.sortBy(visibleOptions(field.value, enumDataType.enumOptions), (x) => x.value)
                : visibleOptions(field.value, enumDataType.enumOptions),
        [field.value, enumDataType.enumOptions],
    );

    return (
        <MemoRadioEditorContents
            options={options}
            entityType={enumDataType}
            fieldName={fieldName}
            fieldFormatter={FieldFormatter}
            subFormatter={subFormatter}
        />
    );
}

function RadioEditorContents({options, entityType, fieldName, fieldFormatter, subFormatter}) {
    return (
        <Wrapper>
            <Radio name={fieldName}>
                {options.map((eo, ix) => {
                    let level = (eo.value.split('/').length - 1) * 4;
                    return (
                        <Radio.Option
                            raw
                            key={ix}
                            level={level}
                            value={eo.value}
                            label={
                                <fieldFormatter.Formatter
                                    entityType={entityType}
                                    formatter={subFormatter}
                                    entity={eo.value}
                                />
                            }
                            name={fieldName}
                        />
                    );
                })}
            </Radio>
        </Wrapper>
    );
}

// We need some performance improvement here, because this RadioEditorContents renders a bunch of options with icons
// and these icons can take about a millisecond each. So if there are 30 options, that's 30ms, which leads to an
// annoying amount of lag when entering text into a textbox
// We can safely memo this component, because given the same props, it always does the exact same thing
const MemoRadioEditorContents = React.memo(RadioEditorContents);

export default EnumRadioEditor;

const Wrapper = styled.div`
    ${FormLabel} {
        > * {
            margin-bottom: var(--space-xsm);
        }
    }
`;
